// Event Impact — quantifies what hub events do to the forecast: per-hub baseline
// (no events) vs with-events, the net change, and rerouted-in/out; plus a card per
// active/upcoming event with its isolated impact, destinations, and a day-by-day
// breakdown (which shows the day-of-week shape of the impact).
//
// "Isolated" impact = forecast with just that one event ([e]) vs no events ([]),
// so each event's effect is attributed cleanly even if several overlap.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Card, Field, Select } from "../../components/ui";
import { getErrorMessage } from "../../lib/errors";
import { addDays, fmtDate, fmtDow, fmtN, today } from "../../lib/format";
import type { Branch, Customer, Hub, HubEvent, HubEventInput } from "../../lib/types";
import { flowShares, hubFlowTotals } from "../../lib/forecast/flowShares";
import { getFlowShareTotals } from "../actuals/branchFlowApi";
import { listBranches } from "../branches/branchesApi";
import {
  effectiveCapacityPct,
  forecastCustomerHub,
  forecastHubDay,
  reroutedInToHub,
  reroutedOutOfHub,
} from "../../lib/forecast/engine";
import { listCustomers } from "../customers/customersApi";
import { listHubs } from "../hubs/hubsApi";
import {
  buildOverrideMap,
  listAccountOverrides,
  type ForecastOverride,
} from "../forecast/overridesApi";
import { listHubEvents } from "./hubEventsApi";
import HubEventForm from "./HubEventForm";

const WINDOWS = [
  { days: 30, label: "Next 30 days" },
  { days: 90, label: "Next 90 days" },
  { days: 365, label: "Next 12 months" },
];

export default function EventImpactPage() {
  const { activeAccount } = useAuth();
  const accountId = activeAccount?.accountId ?? null;

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [flowTotals, setFlowTotals] = useState<{ originHubId: string; branchId: string; volume: number }[]>([]);
  const [overrides, setOverrides] = useState<ForecastOverride[]>([]);
  const [events, setEvents] = useState<HubEvent[]>([]);
  const [winDays, setWinDays] = useState(90);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [start] = useState(today());
  // When a suggested reroute is "applied", pre-fill the event form with it.
  const [creating, setCreating] = useState<Partial<HubEventInput> | null>(null);

  const reload = useCallback(() => {
    if (!accountId) return;
    Promise.all([
      listCustomers(accountId),
      listHubs(accountId),
      listAccountOverrides(accountId),
      listHubEvents(accountId),
      listBranches(accountId),
    ])
      .then(([c, h, ov, ev, b]) => {
        setCustomers(c);
        setHubs(h.filter((x) => x.active));
        setOverrides(ov);
        setEvents(ev);
        setBranches(b.filter((x) => x.active));
      })
      .catch((e) => setError(getErrorMessage(e, "Failed to load event impact.")));
  }, [accountId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // origin→branch flow shares (trailing 4 weeks) → per-hub throughput ratio
  useEffect(() => {
    if (!accountId) return;
    getFlowShareTotals(accountId, addDays(start, -28), addDays(start, -1))
      .then((t) => setFlowTotals(t.map((x) => ({ originHubId: x.originHubId, branchId: x.branchId, volume: x.volume }))))
      .catch(() => {});
  }, [accountId, start]);

  const overrideMap = useMemo(() => buildOverrideMap(overrides), [overrides]);
  const days = useMemo(
    () => Array.from({ length: winDays }, (_, i) => addDays(start, i)),
    [start, winDays]
  );
  const firstDay = days[0];
  const lastDay = days[days.length - 1];
  const hubName = useMemo(() => {
    const m = new Map(hubs.map((h) => [h.id, h.name]));
    return (id: string) => m.get(id) ?? "?";
  }, [hubs]);

  // Events active and overlapping the window.
  const liveEvents = useMemo(
    () =>
      events
        .filter((e) => e.active !== false && e.startDate <= lastDay && e.endDate >= firstDay)
        .sort((a, b) => (a.startDate < b.startDate ? -1 : 1)),
    [events, firstDay, lastDay]
  );

  // Per-hub throughput ratio (what it sorts ÷ what originates here) from the
  // origin→branch flow shares, so the peak load reflects network flow.
  const flowRatioByHub = useMemo(() => {
    const shares = flowShares(flowTotals.map((x) => ({ ...x, date: "" })));
    const own = new Map<string, number>();
    for (const h of hubs) own.set(h.id, days.reduce((s, d) => s + forecastHubDay(customers, h.id, d, overrideMap, []), 0));
    const totals = hubFlowTotals(hubs.map((h) => h.id), branches, own, shares);
    const m = new Map<string, number>();
    for (const [hid, t] of totals) m.set(hid, t.own > 0 ? t.throughput / t.own : 1);
    return m;
  }, [flowTotals, hubs, branches, days, customers, overrideMap]);

  // Per-hub over the window: window sums (baseline vs with events), reroute in/out,
  // and PEAK DAILY throughput (with events) vs the hub's daily capacity.
  const hubStats = useMemo(() => {
    return hubs.map((h) => {
      const ratio = flowRatioByHub.get(h.id) ?? 1;
      let baseline = 0;
      let withEvents = 0;
      let rin = 0;
      let rout = 0;
      let peakBase = 0;
      let peakWith = 0;
      let peakDay = "";
      for (const d of days) {
        const b = forecastHubDay(customers, h.id, d, overrideMap, []);
        const w = forecastHubDay(customers, h.id, d, overrideMap, events);
        baseline += b;
        withEvents += w;
        if (b * ratio > peakBase) peakBase = b * ratio;
        if (w * ratio > peakWith) {
          peakWith = w * ratio;
          peakDay = d;
        }
        if (events.length) {
          rin += reroutedInToHub(customers, h.id, d, events);
          rout += reroutedOutOfHub(customers, h.id, d, events);
        }
      }
      const cap = h.capacity ?? null;
      return {
        hub: h,
        baseline,
        withEvents,
        delta: withEvents - baseline,
        rin,
        rout,
        peakBase,
        peakWith,
        peakDay,
        cap,
        headroom: cap != null ? cap - peakWith : null, // negative = over capacity
      };
    });
  }, [hubs, customers, days, overrideMap, events, flowRatioByHub]);

  const impactRows = useMemo(
    () => hubStats.filter((r) => r.delta !== 0 || r.rin > 0 || r.rout > 0),
    [hubStats]
  );

  const kpi = useMemo(() => {
    const net = impactRows.reduce((s, r) => s + r.delta, 0);
    const moved = impactRows.reduce((s, r) => s + r.rout, 0);
    return { active: liveEvents.length, net, moved };
  }, [impactRows, liveEvents]);

  // Capacity-driven recommendations / flags.
  const recos = useMemo(() => {
    const out: Array<{ tone: "warn" | "ok" | "tip"; text: string }> = [];
    const withCap = hubStats.filter((s) => s.cap != null);
    if (withCap.length === 0) {
      out.push({
        tone: "tip",
        text: "No hub capacities are set — add a daily capacity on the Hubs page to enable headroom checks.",
      });
      return out;
    }
    const over = withCap.filter((s) => (s.headroom ?? 0) < 0);
    const spare = withCap
      .filter((s) => (s.headroom ?? 0) > 0)
      .sort((a, b) => (b.headroom ?? 0) - (a.headroom ?? 0));

    for (const s of over) {
      out.push({
        tone: "warn",
        text: `${s.hub.name} is over capacity — peak ${fmtN(s.peakWith)}/day vs cap ${fmtN(
          s.cap ?? 0
        )} (+${fmtN(-(s.headroom ?? 0))}) around ${fmtDate(s.peakDay)}.`,
      });
    }
    // Reroutes pointing INTO an over-capacity hub.
    const overIds = new Set(over.map((s) => s.hub.id));
    for (const e of liveEvents) {
      for (const r of e.redistributeHubs ?? []) {
        if (overIds.has(r.hubId)) {
          out.push({
            tone: "warn",
            text: `A ${e.type} reroute sends volume into ${hubName(
              r.hubId
            )}, which is already over capacity — consider another destination.`,
          });
        }
      }
    }
    // Optimization tip: move overflow from an over hub to the hub with most spare.
    if (over.length && spare.length) {
      const top = spare[0];
      const totalOver = over.reduce((t, s) => t + -(s.headroom ?? 0), 0);
      out.push({
        tone: "tip",
        text: `${top.hub.name} has ~${fmtN(top.headroom ?? 0)}/day spare — rerouting ${fmtN(
          Math.min(totalOver, top.headroom ?? 0)
        )} from ${over.map((s) => s.hub.name).join(", ")} would relieve the overload.`,
      });
    }
    if (over.length === 0) out.push({ tone: "ok", text: "All hubs with a capacity are within it for this window." });
    return out;
  }, [hubStats, liveEvents, hubName]);

  // Concrete reroute suggestions: for each over-capacity hub, propose specific
  // customer → spare-hub moves (sized to bring the over hub within capacity on its
  // peak day). Greedy: shed the biggest contributors first into the hubs with the
  // most same-day room. Each suggestion maps to ONE by_customer reroute event.
  const suggestions = useMemo(() => {
    type Move = {
      key: string;
      day: string;
      customerId: string;
      customerName: string;
      fromHubId: string;
      fromHub: string;
      toHubId: string;
      toHub: string;
      amount: number; // units shed on the peak day
      movePct: number; // % of this customer's peak-day volume at the from hub
    };
    const out: Move[] = [];
    const over = hubStats.filter((s) => s.cap != null && (s.headroom ?? 0) < 0);
    if (!over.length) return out;

    for (const o of over) {
      const day = o.peakDay;
      if (!day) continue;
      let overage = o.peakWith - (o.cap ?? 0);

      // Same-day spare room at every OTHER capacity hub (mutable, drains as we assign).
      const dests = hubStats
        .filter((s) => s.cap != null && s.hub.id !== o.hub.id)
        .map((s) => ({
          hub: s.hub,
          room: (s.cap ?? 0) - forecastHubDay(customers, s.hub.id, day, overrideMap, events),
        }))
        .filter((d) => d.room > 0);

      // This hub's customers by peak-day contribution, biggest first.
      const contributors = customers
        .map((c) => ({ c, vol: forecastCustomerHub(c, o.hub.id, day, overrideMap, events) }))
        .filter((x) => x.vol > 0.5)
        .sort((a, b) => b.vol - a.vol);

      for (const { c, vol } of contributors) {
        if (overage <= 0.5) break;
        // Pick the destination with the most remaining room.
        const best = dests.reduce<(typeof dests)[number] | null>(
          (m, d) => (d.room > (m?.room ?? 0) ? d : m),
          null
        );
        if (!best || best.room <= 0.5) break;
        const place = Math.min(vol, overage, best.room);
        if (place <= 0.5) continue;
        out.push({
          key: `${o.hub.id}|${c.id}|${best.hub.id}`,
          day,
          customerId: c.id,
          customerName: c.name,
          fromHubId: o.hub.id,
          fromHub: o.hub.name,
          toHubId: best.hub.id,
          toHub: best.hub.name,
          amount: Math.round(place),
          movePct: Math.max(1, Math.min(100, Math.round((place / vol) * 100))),
        });
        best.room -= place;
        overage -= place;
      }
    }
    return out;
  }, [hubStats, customers, overrideMap, events]);

  // Turn a suggestion into a pre-filled by_customer reroute event.
  function applySuggestion(s: (typeof suggestions)[number]) {
    setCreating({
      hubId: s.fromHubId,
      type: "reroute",
      startDate: s.day,
      endDate: s.day,
      capacityPct: 100 - s.movePct, // keep the rest at the origin hub
      mode: "by_customer",
      customerOverrides: [{ customerId: s.customerId, pct: 100 }],
      redistributeVolume: true,
      redistributeHubs: [{ hubId: s.toHubId, pct: 100 }],
      active: true,
      notes: `Suggested reroute: move ~${fmtN(s.amount)} of ${s.customerName} from ${s.fromHub} to ${s.toHub}.`,
    });
  }

  // Customers an event affects at its hub.
  const affectedOf = (e: HubEvent): Customer[] => {
    if (e.mode === "by_customer") {
      const ids = new Set((e.customerOverrides ?? []).map((o) => o.customerId));
      return customers.filter((c) => ids.has(c.id));
    }
    return customers.filter((c) => c.active && c.allocations.some((a) => a.hubId === e.hubId));
  };

  // Isolated impact of one event over its window within the page window.
  function eventImpact(e: HubEvent) {
    const win = days.filter((d) => d >= e.startDate && d <= e.endDate);
    const affected = affectedOf(e);
    let base = 0;
    let wth = 0;
    const daily = win.map((d) => {
      let b = 0;
      let w = 0;
      for (const c of affected) {
        b += forecastCustomerHub(c, e.hubId, d, overrideMap, []);
        w += forecastCustomerHub(c, e.hubId, d, overrideMap, [e]);
      }
      base += b;
      wth += w;
      return { d, base: b, wth: w };
    });
    const dests = (e.redistributeHubs ?? []).map((s) => {
      let amt = 0;
      for (const d of win) amt += reroutedInToHub(customers, s.hubId, d, [e]);
      return { hubId: s.hubId, amt };
    });
    return { win, affected, base, wth, delta: wth - base, daily, dests };
  }

  function toggle(id: string) {
    setExpanded((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }

  const signed = (n: number) => `${n > 0 ? "+" : ""}${fmtN(n)}`;

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold tracking-tight text-slate-900">Event Impact</h1>
      <p className="text-xs text-slate-500">
        What hub events do to the forecast — per hub (baseline vs with events), and per event with a
        day-by-day breakdown. Events scale daily volume and reroute it; they don't change a customer's
        DOW or seasonal factors, so the daily breakdown shows the impact in the day-of-week shape.
      </p>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <Card>
        <div className="max-w-xs">
          <Field label="Window">
            <Select value={String(winDays)} onChange={(e) => setWinDays(Number(e.target.value))}>
              {WINDOWS.map((w) => (
                <option key={w.days} value={w.days}>
                  {w.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </Card>

      {/* KPIs */}
      <div className="grid gap-3 sm:grid-cols-3">
        <Card>
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
            Active events
          </p>
          <p className="mt-1 text-2xl font-semibold text-slate-900">{fmtN(kpi.active)}</p>
        </Card>
        <Card>
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
            Net volume change
          </p>
          <p
            className={`mt-1 text-2xl font-semibold ${
              kpi.net < 0 ? "text-red-600" : kpi.net > 0 ? "text-emerald-600" : "text-slate-900"
            }`}
          >
            {signed(kpi.net)}
          </p>
          <p className="text-[11px] text-slate-400">vs baseline, this window</p>
        </Card>
        <Card>
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
            Volume rerouted
          </p>
          <p className="mt-1 text-2xl font-semibold text-slate-900">{fmtN(kpi.moved)}</p>
          <p className="text-[11px] text-slate-400">moved between hubs</p>
        </Card>
      </div>

      {/* Per-hub impact */}
      <Card className="p-0">
        <div className="border-b border-slate-200 px-5 py-3 text-sm font-medium text-slate-700">
          By hub — baseline vs with events
        </div>
        {impactRows.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">No event impact in this window.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-2 font-medium">Hub</th>
                <th className="px-3 py-2 text-right font-medium">Baseline</th>
                <th className="px-3 py-2 text-right font-medium">With events</th>
                <th className="px-3 py-2 text-right font-medium">Δ</th>
                <th className="px-3 py-2 text-right font-medium">Rerouted in</th>
                <th className="px-5 py-2 text-right font-medium">Rerouted out</th>
              </tr>
            </thead>
            <tbody>
              {impactRows.map((r) => (
                <tr key={r.hub.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-5 py-2 font-medium text-slate-900">{r.hub.name}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmtN(r.baseline)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">{fmtN(r.withEvents)}</td>
                  <td
                    className={`px-3 py-2 text-right tabular-nums ${
                      r.delta < 0 ? "text-red-600" : r.delta > 0 ? "text-emerald-600" : "text-slate-400"
                    }`}
                  >
                    {r.delta === 0 ? "—" : signed(r.delta)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-600">
                    {r.rin > 0 ? `+${fmtN(r.rin)}` : "—"}
                  </td>
                  <td className="px-5 py-2 text-right tabular-nums text-red-600">
                    {r.rout > 0 ? `−${fmtN(r.rout)}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Capacity headroom */}
      <Card className="p-0">
        <div className="border-b border-slate-200 px-5 py-3 text-sm font-medium text-slate-700">
          Capacity headroom · peak-day throughput (with events) vs hub capacity
          <span className="ml-2 text-[11px] font-normal text-slate-400">throughput = what the hub sorts (own re-flowed via lanes)</span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-5 py-2 font-medium">Hub</th>
              <th className="px-3 py-2 text-right font-medium">Capacity / day</th>
              <th className="px-3 py-2 text-right font-medium">Peak day</th>
              <th className="px-3 py-2 text-right font-medium">Headroom</th>
              <th className="px-5 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {hubStats.map((s) => {
              const noCap = s.cap == null;
              const over = !noCap && (s.headroom ?? 0) < 0;
              return (
                <tr key={s.hub.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-5 py-2 font-medium text-slate-900">{s.hub.name}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                    {noCap ? "—" : fmtN(s.cap ?? 0)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                    {fmtN(s.peakWith)}
                    {s.peakDay && <span className="text-slate-400"> · {fmtDate(s.peakDay)}</span>}
                  </td>
                  <td
                    className={`px-3 py-2 text-right tabular-nums ${
                      noCap ? "text-slate-300" : over ? "text-red-600" : "text-emerald-600"
                    }`}
                  >
                    {noCap ? "—" : signed(s.headroom ?? 0)}
                  </td>
                  <td className="px-5 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        noCap
                          ? "bg-slate-100 text-slate-500"
                          : over
                            ? "bg-red-50 text-red-700"
                            : "bg-emerald-50 text-emerald-700"
                      }`}
                    >
                      {noCap
                        ? "no capacity set"
                        : over
                          ? `over by ${fmtN(-(s.headroom ?? 0))}`
                          : "within capacity"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Recommendations */}
      <Card>
        <div className="text-sm font-medium text-slate-700">Recommendations</div>
        <ul className="mt-2 space-y-1.5 text-sm">
          {recos.map((r, i) => (
            <li
              key={i}
              className={
                r.tone === "warn"
                  ? "text-red-700"
                  : r.tone === "ok"
                    ? "text-emerald-700"
                    : "text-amber-700"
              }
            >
              {r.tone === "warn" ? "⚠ " : r.tone === "ok" ? "✓ " : "💡 "}
              {r.text}
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-slate-400">
          Heuristics from current hub capacities + active events. A fuller optimizer — pulling
          actuals and planned loads, and proposing the reroutes automatically — can build on this.
        </p>
      </Card>

      {/* Concrete reroute suggestions */}
      {suggestions.length > 0 && (
        <Card className="p-0">
          <div className="border-b border-slate-200 px-5 py-3 text-sm font-medium text-slate-700">
            Suggested reroutes · specific moves to bring over-capacity hubs within capacity
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-2 font-medium">Move</th>
                <th className="px-3 py-2 text-right font-medium">Amount / day</th>
                <th className="px-3 py-2 text-right font-medium">Share</th>
                <th className="px-3 py-2 font-medium">Peak day</th>
                <th className="px-5 py-2 text-right font-medium" />
              </tr>
            </thead>
            <tbody>
              {suggestions.map((s) => (
                <tr key={s.key} className="border-b border-slate-100 last:border-0">
                  <td className="px-5 py-2 text-slate-900">
                    <span className="font-medium">{s.customerName}</span>{" "}
                    <span className="text-slate-400">·</span> {s.fromHub}{" "}
                    <span className="text-brand-600">→</span> {s.toHub}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                    {fmtN(s.amount)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{s.movePct}%</td>
                  <td className="px-3 py-2 text-slate-500">{fmtDate(s.day)}</td>
                  <td className="px-5 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => applySuggestion(s)}
                      className="rounded-lg border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100"
                    >
                      Create reroute
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-5 py-3 text-xs text-slate-400">
            Each suggestion moves a single customer's volume to the hub with the most spare room on
            the over hub's peak day. "Create reroute" opens a pre-filled event for that one day —
            review the dates and share, then save. Re-check this page afterward to confirm the hub is
            within capacity.
          </p>
        </Card>
      )}

      {/* Per-event cards */}
      <div className="space-y-3">
        <div className="text-sm font-medium text-slate-700">Events ({liveEvents.length})</div>
        {liveEvents.length === 0 && (
          <Card>
            <p className="text-sm text-slate-500">No active or upcoming events in this window.</p>
          </Card>
        )}
        {liveEvents.map((e) => {
          const imp = eventImpact(e);
          const open = expanded.has(e.id);
          const scope =
            e.mode === "by_customer"
              ? `${(e.customerOverrides ?? []).length} customer${
                  (e.customerOverrides ?? []).length === 1 ? "" : "s"
                }`
              : "whole hub";
          return (
            <Card key={e.id} className="p-0">
              <button
                type="button"
                onClick={() => toggle(e.id)}
                className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left hover:bg-slate-50"
              >
                <div>
                  <div className="text-sm font-medium text-slate-900">
                    <span className="capitalize">{e.type}</span> @ {hubName(e.hubId)}{" "}
                    <span className="font-normal text-slate-400">
                      · {fmtDate(e.startDate)}–{fmtDate(e.endDate)} · {scope} ·{" "}
                      {effectiveCapacityPct(e)}% kept
                    </span>
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    Impact: <span className={imp.delta < 0 ? "text-red-600" : "text-emerald-600"}>
                      {imp.delta === 0 ? "no change" : signed(imp.delta)}
                    </span>
                    {imp.dests.length > 0 &&
                      ` → ${imp.dests
                        .filter((d) => d.amt > 0)
                        .map((d) => `${hubName(d.hubId)} +${fmtN(d.amt)}`)
                        .join(", ")}`}
                  </div>
                </div>
                <span className="text-xs text-brand-600">{open ? "Hide days ▾" : "Show days ▸"}</span>
              </button>
              {open && (
                <table className="w-full border-t border-slate-100 text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-5 py-2 font-medium">Day</th>
                      <th className="px-3 py-2 text-right font-medium">Baseline</th>
                      <th className="px-3 py-2 text-right font-medium">With event</th>
                      <th className="px-5 py-2 text-right font-medium">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {imp.daily.map((row) => {
                      const dl = row.wth - row.base;
                      return (
                        <tr key={row.d} className="border-b border-slate-100 last:border-0">
                          <td className="px-5 py-1.5 text-slate-700">{fmtDow(row.d)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">
                            {fmtN(row.base)}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">
                            {fmtN(row.wth)}
                          </td>
                          <td
                            className={`px-5 py-1.5 text-right tabular-nums ${
                              dl < 0 ? "text-red-600" : dl > 0 ? "text-emerald-600" : "text-slate-400"
                            }`}
                          >
                            {dl === 0 ? "—" : signed(dl)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </Card>
          );
        })}
      </div>

      {creating && (
        <HubEventForm
          accountId={accountId}
          event={null}
          initial={creating}
          hubs={hubs}
          customers={customers}
          onClose={() => setCreating(null)}
          onSaved={() => {
            setCreating(null);
            reload();
          }}
        />
      )}
    </div>
  );
}
